import { Component, Input, Output, EventEmitter } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList } from 'immutable';
import { debounce } from 'lodash-decorators';
import * as constants from '../../constants-types/constants';
import { List } from '../../constants-types/PiList.model';
import { FilterInfo, FilterType } from '../../core-data-model/FilterInfo.model';
import { Group } from '../../core-data-model/Group.model';
import { IconSize } from '../../core-data-model/IconSize';
import { IconType } from '../../core-data-model/IconType';
import { Job } from '../../core-data-model/Job.model';
import { Person } from '../../core-data-model/Person.model';
import { PizzaPlaygroundSession } from '../../core-data-model/Strategy.model';
import { TypesUtility } from '../../core-data-model/Types.utility';
import { GroupsDataService } from '../../core-services/Groups.dataservice';
import { SearchApi } from '../../core-services/SearchApi.connect';
import { ArrayUtilities } from '../../core-utilities/Array.utility';
import { DateFormatUtility } from '../../core-utilities/DateFormat.utility';
import { TranslationService } from '../../translations';

/* Comments about this file:

trash 2 hours ago
Why wouldn't this just be a list of Persons? It seems kind of garbage to allow
 either a list of a thing or a list of lists of things. This is v1 of this component
 and we're already starting off with what, at first glance, seems like a hacked use-case.

  @trash
trash 2 hours ago
Following up on our brief convo about this:

One thing we could do is similar to how we separated the pillbox that handles a list
of things vs the pillbox that handles just one thing (ngModel bound to array vs single
    object), we should move the conversion from group to a list of people out into a
    parent component that does just that. That will enable the API for this component
    to be a lot cleaner so it can just focus on displaying a list of people. Instead of
    sometimes a group and sometimes some people.

*/
@Component({
    selector: 'people-selector',
    template: `
        <ok-pillbox-with-typeahead
            [(ngModel)]="listOfPeople"
            (ngModelChange)="modifyIntermediateList()"
            (typeaheadOnTextChange)="typeaheadOnTextChange($event)"
            [typeaheadList]="typeaheadList"
            [typeaheadPlaceholder]="placeholder"
            [isSelected]="isSelected"
            (onPillSelect)="onPillSelect.emit($event)"
            [searchStyling]="true"
            [isLoadingPossibilities]="loadingPossibilities"
            [isColorPickerEnabled]="isColorPickerEnabled"
            (onColorChange)="onColorChange.emit()"
        >
            <ng-template #pillbox let-item="item">
                @if (type.isPerson(item)) {
                    @if (item.id === personLoadingId) {
                        <abstract-icon
                            [iconType]="IconType.Spinner"
                            [iconSize]="IconSize.Medium"
                        >
                        </abstract-icon>
                    }
                    @if (item.id !== personLoadingId) {
                        <abstract-icon
                            class="ok-pill-checkmark"
                            [iconType]="IconType.Check"
                            [iconSize]="IconSize.Small"
                            style="margin-right:5px"
                        >
                        </abstract-icon>
                        @if (item.hasBehavioralPattern()) {
                            @if (item.referencePattern) {
                                <abstract-icon
                                    [iconType]="
                                        item.referencePattern.icon
                                            .abstractIconSVG
                                    "
                                    [iconSize]="IconSize.M_large"
                                    style="margin-right: 6px;border: 3px solid white;border-radius: 50%;background-color: white;"
                                    title="{{
                                        TR.key(item.referencePattern.key)
                                            | titlecase
                                    }}"
                                >
                                </abstract-icon>
                            }
                        } @else {
                            <abstract-icon
                                [iconType]="IconType.Warning2"
                                [iconSize]="21"
                                title="{{
                                    TR.key('label.common-terms.no.pattern')
                                }}"
                                style="margin-right:5px;"
                            >
                            </abstract-icon>
                        }
                        {{ item.fullName() }}
                        @if (item.email && !concisePillboxNames) {
                            ({{ item.email }})
                        }
                    }
                }
                <!-- TODO job selection should be disallowed in this component -->
                @if (type.isJob(item)) {
                    {{ displayJob(item) }}
                }
            </ng-template>
            <ng-template #typeahead let-item="item">
                @if (type.isPerson(item)) {
                    {{ item.fullName() }}
                    @if (item.email) {
                        ({{ item.email }})
                    }
                    @if (!item.hasBehavioralPattern()) {
                        [{{ TR.key('label.common-terms.no.pattern') }}]
                    }
                }
                @if (type.isGroup(item)) {
                    {{ item.name }} [ {{ TR.key('common.team') }} ]
                }
                @if (type.isJob(item)) {
                    {{ item.title }}
                }
            </ng-template>
        </ok-pillbox-with-typeahead>
    `,
    // Following is required for ControlValueAccessor
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: PeopleSelectorComponent,
            multi: true
        }
    ],
    standalone: false
})
export class PeopleSelectorComponent {
    IconType = IconType;
    IconSize = IconSize;

    readonly personLoadingId = '-';

    @Input()
    placeholder: string;
    @Input()
    isSelected: (input: any) => boolean;

    @Input()
    concisePillboxNames: boolean;

    @Input()
    searchFilters: FilterInfo[] = [new FilterInfo({ type: FilterType.People })];

    @Input()
    isGroupsExcluded = false;

    @Input()
    isColorPickerEnabled: boolean;

    @Input()
    pizzaSession: PizzaPlaygroundSession;

    @Output()
    onPillSelect: EventEmitter<any> = new EventEmitter();
    @Output()
    onColorChange = new EventEmitter<void>();

    listOfPeople: List<Person | Job | Group>;
    typeaheadList: List<Person | Job | Group> = ImmutableList();
    loadingPossibilities = false;

    constructor(
        public TR: TranslationService,
        private searchApi: SearchApi,
        private groupsService: GroupsDataService,
        public type: TypesUtility,
        public date: DateFormatUtility,
        private arrayUtil: ArrayUtilities
    ) {}

    // TODO - break this out

    typeaheadOnTextChange(textInBox: string) {
        if (textInBox) {
            this.loadingPossibilities = true;
            this.searchPeopleAndJobs(textInBox);
        } else {
            this.typeaheadList = ImmutableList();
        }
    }

    modifyIntermediateList() {
        Promise.all(
            this.listOfPeople
                .filter((item) => item instanceof Group)
                .map((group) => this.addGroup(group as Group))
                .toArray()
        ).then((peopleInGroups) => {
            this.boundValue = this.boundValue.filter(
                (item) =>
                    (item instanceof Person || item instanceof Job) &&
                    item.id !== this.personLoadingId
            );
            this.boundValue = this.addSetOfPeople(peopleInGroups);
        });
        const stubsToAdd = this.listOfPeople.reduce(
            (accumulator, item) =>
                (accumulator +=
                    item instanceof Group ? item.peopleAndJobCount : 0),
            0
        );
        const stubs = Array.from(Array(stubsToAdd).keys()).map(
            () => new Person({ id: this.personLoadingId })
        );

        // We don't want to propagate the stubs up because they can cause errors due
        // to their use of an invalid id
        this.setBoundValue(
            this.listOfPeople
                .filter((item) => item instanceof Person || item instanceof Job)
                .push(...stubs),
            false
        );
    }

    displayJob(item: Job) {
        return `${item.title} ${this.date.transformDate(item.lastUpdated)}`;
    }

    @debounce(constants.peopleSearchDelay)
    private searchPeopleAndJobs(searchText: string): void {
        this.searchApi
            .getResults(searchText, this.searchFilters)
            .then((results) => {
                const idHash = this.getListHash();
                this.bindPersonWithColor(results);
                this.typeaheadList = ImmutableList(results).filter(
                    (result) => !idHash[result.id]
                );
                if (!this.isGroupsExcluded)
                    this.typeaheadList = this.typeaheadList.concat(
                        this.groupsService.getGroupsWithNameMatch(searchText)
                    );
            })
            .finally(() => {
                this.loadingPossibilities = false;
            });
    }

    private getListHash() {
        const idHash: { [key: string]: boolean } = {};
        this.listOfPeople.forEach((person) => {
            idHash[person.id] = true;
        });
        return idHash;
    }

    private addGroup(group: Group): Promise<(Person | Job)[]> {
        return this.groupsService
            .getGroupContentsByGroup(group, this.searchFilters)
            .then((results) => {
                this.bindPersonWithColor(results);
                return results;
            });
    }

    private addSetOfPeople(
        peopleInGroups: (Person | Job)[][]
    ): List<Person | Job | Group> {
        const idHash = this.getListHash();
        const peopleToAdd = this.arrayUtil
            .flat(peopleInGroups)
            .filter((person) => !idHash[person.id]);
        return this.listOfPeople.concat(peopleToAdd);
    }

    private bindPersonWithColor(results: (Person | Job)[]) {
        if (
            this.isColorPickerEnabled &&
            this.pizzaSession &&
            // Make sure to the handle the fact that the pizza session could not exist
            // AND the fact that the personIdsWithColor object can come back as null from API
            this.pizzaSession?.personIdsWithColor !== null
        ) {
            results.forEach((item) => {
                if (item instanceof Person) {
                    const matchedPersonIdWithColor =
                        this.pizzaSession.personIdsWithColor.find(
                            (idWithColor) => idWithColor.personId === item.id
                        );
                    if (matchedPersonIdWithColor) {
                        item.displayColor =
                            matchedPersonIdWithColor.displayColor;
                    }
                }
            });
        }
    }

    /** ***************************************/
    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    private propagateChange: Function = () => {
        /* No-op required by ControlValueAccessor interface - see registerOnChange */
    };

    get boundValue(): List<Person | Job | Group> {
        return this.listOfPeople;
    }
    set boundValue(val: List<Person | Job | Group>) {
        this.setBoundValue(val);
    }

    // Support optionally not propagating changes up outside this component
    private setBoundValue(
        val: List<Person | Job | Group>,
        propagateChange = true
    ) {
        this.listOfPeople = val;
        if (propagateChange) {
            this.propagateChange(this.listOfPeople);
        }
    }

    /* Meet ControlValueAccessor Interface */
    // Do NOT propagate changes up here
    writeValue(value: List<Person>): void {
        this.listOfPeople = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed: any) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
